import { Secret } from 'game-logic';
import { sha256 } from 'js-sha256';
import { GameStatesData } from 'src/app/core/api/dealer/schemas/game-states';
import { UxGameServices } from 'src/app/core/services/ux-game/ux-game-services';
import { CheckpointAction } from './checkpoint-action';
import { CheckpointJobs } from './checkpoint-jobs';
import { PAGE } from 'src/app/routes';

export class CheckpointCreator {
  public get cachedMoves(): string[] {
    return this.serverMoves;
  }

  private actionQueue: CheckpointAction[] = [];
  private processingPromise: Promise<void> | null = null;

  private dealerUrl: string | undefined = undefined;
  private dealId: string | undefined = undefined;
  private prevCheckpointId: string | null = null;
  private secrets = new Map<number, number>();
  private serverMoves: string[] = [];
  private clientMoves: string[] = [];
  private pollInterval: NodeJS.Timeout | null;
  private lastServerSyncTimestamp: number | null = null;
  private newMovesToSkip = 0;
  private isGameComplete = false;
  private scoreHasChanged = false;

  private readonly maxElapsedDurationMs = 5000;
  private readonly maxChunkSize = 32;

  constructor(
    private services: UxGameServices,
    private checkpointJobs: CheckpointJobs,
  ) {
    this.pollInterval = setInterval(() => this.checkForUnsentMoves(), 1000);
  }

  public onDestroy(): void {
    this.stopTimer();
  }

  public setDealer(dealerUrl: string, dealId: string): void {
    this.dealerUrl = dealerUrl;
    this.dealId = dealId;
  }

  public async initialMoves(): Promise<string[]> {
    const initialGameState = await this.fetchGameState();

    this.serverMoves = [...(initialGameState?.moves ?? [])];
    this.clientMoves = [...(initialGameState?.moves ?? [])];

    this.newMovesToSkip = this.clientMoves.length;
    this.lastServerSyncTimestamp = Date.now();

    initialGameState?.disclosures.forEach((disclosure) => {
      this.secrets.set(disclosure.index, disclosure.value);
    });

    return initialGameState?.moves ?? [];
  }

  public onNewMove(moves: string[]): void {
    const newMoves = [...moves];

    while (newMoves.length > 0 && this.newMovesToSkip > 0) {
      newMoves.shift();
      this.newMovesToSkip--;
    }

    if (newMoves.length > 0) {
      this.enqueueAction({ type: 'NewMove', payload: { moves: newMoves } });
    }
  }

  public onScoreChanged(_score: number): void {
    this.scoreHasChanged = true;
  }

  public onRequestSecrets(indexes: number[]): void {
    this.enqueueAction({ type: 'SecretsRequested', payload: { indexes } });
  }

  public waitForCheckpoints(): Promise<void> {
    if (this.processingPromise) {
      return this.processingPromise;
    }

    if (this.actionQueue.length > 0) {
      this.processingPromise = this.processQueue();
      return this.processingPromise;
    }

    return Promise.resolve();
  }

  public async fetchSecrets(indexes: number[]): Promise<Secret[]> {
    try {
      return indexes.map((index) => {
        const value = this.secrets.get(index);
        if (value === undefined) {
          throw new Error(`Invalid checkpoint value for index: ${index}`);
        }
        return {
          index,
          value,
        };
      });
    } catch (error) {
      this.services.analyticsService.handleError(error, 'Fetch secrets', { indexes });

      const alert = await this.services.alertController.create({
        header: this.services.translocoService.translate('service.alert.fatal-reload-error-title'),
        message: this.services.translocoService.translate(
          'service.alert.fatal-reload-error-message',
        ),
        cssClass: 'alert',
        buttons: [
          {
            text: this.services.translocoService.translate('service.alert.ok-button'),
            cssClass: 'alert-button',
            handler: async () => {
              this.services.router.navigate([PAGE.LOADING]);
            },
          },
        ],
        backdropDismiss: false,
      });
      await alert.present();
      await alert.onDidDismiss();

      throw error;
    }
  }

  public async gameComplete(): Promise<void> {
    this.stopTimer();
    this.isGameComplete = true;

    await this.waitForCheckpoints();
  }

  private checkForUnsentMoves(): void {
    if (!this.lastServerSyncTimestamp) {
      return;
    }

    if (Date.now() - this.lastServerSyncTimestamp > this.maxElapsedDurationMs) {
      this.waitForCheckpoints(); // Do not await
    }
  }

  private async processQueue(): Promise<void> {
    try {
      const forceInternet = this.scoreHasChanged;
      this.scoreHasChanged = false;

      do {
        const newMoves: string[] = [];
        const requestedSecrets: number[] = [];

        let canBatchActions = true;
        while (this.actionQueue.length > 0 && canBatchActions) {
          const action = this.actionQueue.shift();
          if (!action) {
            continue;
          }

          switch (action.type) {
            case 'NewMove':
              newMoves.push(...action.payload.moves);
              break;
            case 'SecretsRequested':
              requestedSecrets.push(...action.payload.indexes);

              if (this.actionQueue.length > 0) {
                // Peek without removing
                if (this.actionQueue[0].type === 'NewMove') {
                  // If next action is NewMove, process current batch before continuing
                  canBatchActions = false;
                }
              }
              break;
          }
        }

        if (requestedSecrets.length > 0 || newMoves.length > 0) {
          await this.batchCheckpointData(requestedSecrets, newMoves, forceInternet);
        }
      } while (this.actionQueue.length > 0);
    } finally {
      this.processingPromise = null;
    }
  }

  private enqueueAction(action: CheckpointAction): void {
    if (this.isGameComplete) {
      return;
    }
    this.actionQueue.push(action);
  }

  private stopTimer(): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
    this.pollInterval = null;
  }

  private async batchCheckpointData(
    requestedIndexes: number[],
    newMoves: string[],
    forceInternet: boolean,
  ): Promise<void> {
    // Push all new moves
    this.clientMoves.push(...newMoves);

    // make sure to take a copy
    const unsentIndexes = requestedIndexes.slice();
    const unsentMoves = this.clientMoves.slice(this.serverMoves.length);

    if (unsentMoves.length === 0 && unsentIndexes.length === 0) {
      this.lastServerSyncTimestamp = Date.now();
      return;
    }

    const requiresInternet =
      unsentMoves.length > this.maxChunkSize || unsentIndexes.length > 0 || forceInternet;

    while (unsentMoves.length > 0 || unsentIndexes.length > 0) {
      const chunkMoves = unsentMoves.splice(0, this.maxChunkSize);

      const gameHash = CheckpointCreator.computeHash(
        [...this.serverMoves, ...chunkMoves].join('\n'),
      );

      if (unsentMoves.length > 0) {
        await this.sendCheckpoints(gameHash, chunkMoves, [], requiresInternet);
      } else {
        const chunkDisclosures = unsentIndexes.splice(0, this.maxChunkSize);
        await this.sendCheckpoints(gameHash, chunkMoves, chunkDisclosures, requiresInternet);
      }
    }
  }

  private async fetchGameState(): Promise<GameStatesData> {
    if (!this.dealerUrl || !this.dealId) {
      throw new Error(`Invalid deal`);
    }

    const result = await this.checkpointJobs.requestGameStates(this.dealerUrl, this.dealId);
    this.prevCheckpointId = result.lastCheckpointId;

    return result;
  }

  private async sendCheckpoints(
    gameHash: string,
    unsentMoves: string[],
    indexes: number[],
    requiresInternet: boolean,
  ): Promise<void> {
    if (!this.dealerUrl || !this.dealId) {
      throw new Error(`Invalid deal`);
    }

    const result = await this.checkpointJobs.requestCheckpoints(
      this.dealerUrl,
      this.dealId,
      gameHash,
      unsentMoves,
      indexes,
      this.isGameComplete || requiresInternet,
      this.prevCheckpointId,
    );
    if (result === undefined) {
      return;
    }

    this.prevCheckpointId = result.checkpointId;
    this.serverMoves.push(...unsentMoves);

    result.secrets.forEach((secret) => {
      this.secrets.set(secret.index, secret.value);
    });

    this.lastServerSyncTimestamp = Date.now();
  }

  private static computeHash(output: string): string {
    return sha256.create().update(output).hex();
  }
}
